import { createFileRoute, Link } from "@tanstack/react-router";
import { trackConversion } from "@/lib/gtag";
import { MapPin, Phone, ArrowRight } from "lucide-react";
import { SUBURBS, type Suburb } from "@/lib/suburbs";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StickyMobileBar } from "@/components/StickyMobileBar";
import { IdleCallNudge } from "@/components/IdleCallNudge";

const AIRPORT_SLUGS = new Set([
  "melbourne-airport",
  "tullamarine",
  "essendon",
  "keilor",
  "airport-west",
  "gladstone-park",
  "westmeadows",
  "attwood",
]);
const EAST_SLUGS = new Set([
  "box-hill",
  "doncaster",
  "ringwood",
  "blackburn",
  "camberwell",
  "hawthorn",
  "kew",
  "balwyn",
  "mitcham",
  "croydon",
]);

function groupOf(s: Suburb): "airport" | "east" | "south-east" {
  if (AIRPORT_SLUGS.has(s.slug)) return "airport";
  if (EAST_SLUGS.has(s.slug)) return "east";
  return "south-east";
}

export const Route = createFileRoute("/suburbs/")({
  head: () => ({
    meta: [
      { title: "South East Melbourne Suburbs We Serve — SE Premium Cabs" },
      {
        name: "description",
        content:
          "Premium taxi services across South East Melbourne, including Dandenong and Frankston. Book 24/7 airport transfers and corporate travel.",
      },
      { property: "og:title", content: "South East Melbourne Suburbs — SE Premium Cabs" },
      {
        property: "og:description",
        content:
          "Premium taxi service across South East Melbourne — sedans, SUVs and Maxi taxis. Airport transfers, corporate travel and 24/7 bookings.",
      },
      { property: "og:url", content: "https://sepremiumcabs.com/suburbs" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://sepremiumcabs.com/og-image.jpg" },
      { name: "twitter:image", content: "https://sepremiumcabs.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://sepremiumcabs.com/suburbs" }],
  }),
  component: SuburbsIndex,
});

function SuburbsIndex() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader active="suburbs" />

      <main className="mx-auto max-w-[1400px] px-6 py-12">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold tracking-[0.3em] text-primary">
            SERVICE AREAS
          </div>
          <h1 className="mt-3 text-4xl font-bold tracking-tight md:text-5xl">
            Melbourne Suburbs We Serve
          </h1>
          <p className="mt-4 text-muted-foreground">
            SE Premium Cabs provides 24/7 chauffeured taxi service across Melbourne's
            south east and eastern suburbs, plus fast, fixed-price transfers to and from
            Melbourne Airport. Book a Sedan, SUV or Maxi below.
          </p>
        </div>

        {(
          [
            {
              key: "airport",
              title: "Melbourne Airport & Nearby Suburbs",
              intro:
                "24/7 transfers to Melbourne Airport (Tullamarine) — flight tracking and meet-and-greet included.",
            },
            {
              key: "south-east",
              title: "South East Melbourne Suburbs",
              intro:
                "From Brighton and Caulfield through to Pakenham and Frankston.",
            },
            {
              key: "east",
              title: "Eastern Melbourne Suburbs",
              intro:
                "Serving Box Hill, Doncaster, Ringwood, Camberwell and along the eastern corridor.",
            },
          ] as const
        ).map((group) => {
          const items = SUBURBS.filter((s) => groupOf(s) === group.key);
          if (items.length === 0) return null;
          return (
            <section key={group.key} className="mt-12">
              <h2 className="text-2xl font-semibold">{group.title}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{group.intro}</p>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((s) => (
                  <Link
                    key={s.slug}
                    to="/suburbs/$suburb"
                    params={{ suburb: s.slug }}
                    className="group rounded-lg border border-border/60 bg-secondary/40 p-5 transition-colors hover:border-primary"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-primary" />
                          <h3 className="text-lg font-semibold">{s.name}</h3>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          VIC {s.postcode}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
                    </div>
                    <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">
                      {s.blurb}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}

        <div className="mt-12 rounded-lg border border-primary/40 bg-secondary/40 p-6 text-center">
          <h2 className="text-xl font-semibold">Don't see your suburb?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            We cover all of South East Melbourne. Call us and we'll be there.
          </p>
          <a
            href="tel:0493100265"
            onClick={() => trackConversion("phoneCall")}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
          >
            <Phone className="h-4 w-4" />
            Call 0493 100 265
          </a>
        </div>
        <div className="h-20 lg:hidden" />
      </main>

      <SiteFooter />
      <StickyMobileBar />
      <IdleCallNudge />
    </div>
  );
}